import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getFramework, previewSteps } from "@/api/frameworks";
import { beginAndPostRun } from "./active-runs-store";
import { setPendingPlacement } from "./placement-handoff";
import { getLanguagePlacementStatus } from "@/api/learner";
import type { FrameworkCost, StartInputs } from "@/api/create-types";
import { FORMS, presentationFor, type FieldSpec, type FrameworkForm } from "./framework-config";
import { SetupForm } from "./setup-form";
import { LectureStart } from "./lecture/lecture-start";
import { Alert, Spinner } from "@/components/ui";

function CostSummary({ cost }: { cost: FrameworkCost }) {
  const { t } = useTranslation();
  const bits: string[] = [];
  if (cost.expected_questions != null) bits.push(t("~{{count}} questions", { count: cost.expected_questions }));
  if (cost.credits_point != null) bits.push(t("~{{count}} credits", { count: cost.credits_point }));
  else if (cost.credits_low != null && cost.credits_high != null)
    bits.push(t("{{low}}–{{high}} credits", { low: cost.credits_low, high: cost.credits_high }));
  return (
    <div className="card p-4">
      <div className="flex items-center gap-2">
        <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold capitalize text-brand-700">
          {t("{{level}} cost", { level: cost.level })}
        </span>
        {bits.length > 0 && <span className="text-sm text-ink-faint">{bits.join(" · ")}</span>}
      </div>
      {cost.note && <p className="mt-2 text-sm text-ink-faint">{cost.note}</p>}
    </div>
  );
}

/** Build a generic form for user-authored frameworks from preview-steps. */
function genericForm(fields: FieldSpec[], t: (k: string, o?: Record<string, unknown>) => string): FrameworkForm {
  return {
    fields,
    validate: (inputs: StartInputs) => {
      for (const f of fields) {
        if (f.required && !String(inputs[f.key] ?? "").trim())
          return t("{{label}} is required.", { label: f.label });
      }
      return null;
    },
  };
}

export function FrameworkDetailPage() {
  const { t } = useTranslation();
  const { slug = "" } = useParams();
  const navigate = useNavigate();

  const fwQuery = useQuery({
    queryKey: ["framework", slug],
    queryFn: () => getFramework(slug),
    enabled: !!slug,
  });

  const hasBespokeForm = slug in FORMS;
  // Only user-authored / unknown frameworks need preview-steps to discover inputs.
  const previewQuery = useQuery({
    queryKey: ["preview-steps", slug],
    queryFn: () => previewSteps(slug, { language: "en" }),
    enabled: !!slug && !hasBespokeForm && slug !== "lecture",
  });

  const form: FrameworkForm | null = useMemo(() => {
    if (hasBespokeForm) return FORMS[slug];
    if (!previewQuery.data) return null;
    const fields: FieldSpec[] = previewQuery.data.required_start_inputs
      .filter((f) => ["string", "integer", "number", "boolean"].includes(f.type))
      .map((f) => ({
        key: f.name,
        label: f.name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        kind: f.type === "boolean" ? "toggle" : f.type === "string" ? "textarea" : "number",
        required: f.required,
      }));
    return genericForm(fields, t);
  }, [hasBespokeForm, slug, previewQuery.data, t]);

  // Pure generation: fire the POST in the background (it's synchronous and can
  // block for the whole generation) and land on the Create gallery immediately,
  // where a placeholder pill shows "Generating…". (Marking flows that pause for
  // input navigate to the run page themselves — see setup-form/lecture-start.)
  const startGeneration = async (inputs: StartInputs) => {
    const title = fwQuery.data?.title ?? slug;
    // Language-trainer: if we don't yet know the learner's level for this
    // target language, run a standalone placement quiz FIRST (separate from the
    // normal bulk training), then start the real run with the placed level.
    if (slug === "language-trainer") {
      const lang = String(inputs.target_language ?? "").trim();
      if (lang) {
        try {
          const status = await getLanguagePlacementStatus(lang);
          if (status.needs_placement) {
            setPendingPlacement({
              language: lang,
              frameworkSlug: slug,
              frameworkTitle: title,
              startInputs: inputs,
            });
            navigate("/app/create/placement");
            return;
          }
        } catch {
          // Status check failed — fall through and start the run as usual.
        }
      }
    }
    beginAndPostRun({ frameworkSlug: slug, frameworkTitle: title, startInputs: inputs });
    navigate("/app/create");
  };

  const p = presentationFor(slug);

  return (
    <div className="mx-auto max-w-2xl animate-fade-up lg:max-w-4xl">
      <Link to="/app/create" className="text-sm text-brand-600 hover:underline">
        {t("← All frameworks")}
      </Link>

      {fwQuery.isLoading && (
        <div className="flex justify-center py-20 text-brand-600">
          <Spinner className="h-7 w-7" />
        </div>
      )}
      {fwQuery.isError && (
        <div className="mt-6">
          <Alert>
            {fwQuery.error instanceof Error ? fwQuery.error.message : t("Framework not found.")}
          </Alert>
        </div>
      )}

      {fwQuery.data && (
        <>
          <div className="mt-4 flex items-start gap-4">
            <div className={`flex h-14 w-14 items-center justify-center rounded-2xl text-3xl ${p.tile}`}>
              {p.emoji}
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{fwQuery.data.title}</h1>
              <p className="mt-1 text-ink-faint">{fwQuery.data.description || t(p.tagline)}</p>
            </div>
          </div>

          <div className="mt-5">
            <CostSummary cost={fwQuery.data.cost_estimate} />
          </div>

          <div className="mt-8">
            {slug === "lecture" ? (
              <LectureStart />
            ) : (
              <>
                {previewQuery.isLoading && (
                  <div className="flex justify-center py-10 text-brand-600">
                    <Spinner className="h-6 w-6" />
                  </div>
                )}
                {form && (
                  <SetupForm
                    slug={slug}
                    frameworkTitle={fwQuery.data.title}
                    form={form}
                    submitting={false}
                    serverError={null}
                    onSubmit={startGeneration}
                  />
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
